import { apiClient } from './client';
import type { Tenant, TenantCreate, TenantUpdate, ApiKey, ApiKeyCreate, ApiKeyCreated, TenantSecret, SecretCreate, SecretUpdate, SecretActionResponse, TelegramGroup, TelegramGroupCreate, TelegramGroupUpdate, EmailConfig, EmailConfigCreate, EmailConfigUpdate, EmailConfigItemCreate, EmailConfigItemUpdate } from '@/types';

export const tenantApi = {
  list: async (): Promise<Tenant[]> => {
    const { data } = await apiClient.get<Tenant[]>('/api/v1/admin/tenants');
    return data;
  },
  get: async (id: string): Promise<Tenant> => {
    const { data } = await apiClient.get<Tenant>(`/api/v1/admin/tenants/${id}`);
    return data;
  },
  create: async (body: TenantCreate): Promise<Tenant> => {
    const { data } = await apiClient.post<Tenant>('/api/v1/admin/tenants', body);
    return data;
  },
  update: async (id: string, body: TenantUpdate): Promise<Tenant> => {
    const { data } = await apiClient.patch<Tenant>(`/api/v1/admin/tenants/${id}`, body);
    return data;
  },

  // Logo
  uploadLogo: async (id: string, file: File): Promise<Tenant> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.put<Tenant>(`/api/v1/admin/tenants/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
  deleteLogo: async (id: string): Promise<Tenant> => {
    const { data } = await apiClient.delete<Tenant>(`/api/v1/admin/tenants/${id}/logo`);
    return data;
  },

  // Plantilla de cotizacion (.docx por tenant, permiso platform.tenant_assets.manage)
  uploadQuotationTemplate: async (id: string, file: File): Promise<Tenant> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.put<Tenant>(`/api/v1/admin/tenants/${id}/quotation-template`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
  downloadQuotationTemplate: async (id: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/api/v1/admin/tenants/${id}/quotation-template`, {
      responseType: 'blob',
    });
    return data;
  },
  deleteQuotationTemplate: async (id: string): Promise<Tenant> => {
    const { data } = await apiClient.delete<Tenant>(`/api/v1/admin/tenants/${id}/quotation-template`);
    return data;
  },

  // API Keys
  listApiKeys: async (tenantId: string): Promise<ApiKey[]> => {
    const { data } = await apiClient.get<ApiKey[]>(`/api/v1/admin/tenants/${tenantId}/api-keys`);
    return data;
  },
  createApiKey: async (tenantId: string, body: ApiKeyCreate): Promise<ApiKeyCreated> => {
    const { data } = await apiClient.post<ApiKeyCreated>(`/api/v1/admin/tenants/${tenantId}/api-keys`, body);
    return data;
  },
  deleteApiKey: async (tenantId: string, apiKeyId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/tenants/${tenantId}/api-keys/${apiKeyId}`);
  },

  // Secrets
  listSecrets: async (tenantId: string): Promise<TenantSecret[]> => {
    const { data } = await apiClient.get<TenantSecret[]>(`/api/v1/admin/tenants/${tenantId}/secrets`);
    return data;
  },
  createSecret: async (tenantId: string, body: SecretCreate): Promise<SecretActionResponse> => {
    const { data } = await apiClient.post<SecretActionResponse>(`/api/v1/admin/tenants/${tenantId}/secrets`, body);
    return data;
  },
  updateSecret: async (tenantId: string, secretId: string, body: SecretUpdate): Promise<SecretActionResponse> => {
    const { data } = await apiClient.patch<SecretActionResponse>(`/api/v1/admin/tenants/${tenantId}/secrets/${secretId}`, body);
    return data;
  },
  deleteSecret: async (tenantId: string, secretId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/tenants/${tenantId}/secrets/${secretId}`);
  },

  // Telegram Groups
  listTelegramGroups: async (tenantId: string): Promise<TelegramGroup[]> => {
    const { data } = await apiClient.get<TelegramGroup[]>(`/api/v1/admin/tenants/${tenantId}/telegram-groups`);
    return data;
  },
  createTelegramGroup: async (tenantId: string, body: TelegramGroupCreate): Promise<TelegramGroup> => {
    const { data } = await apiClient.post<TelegramGroup>(`/api/v1/admin/tenants/${tenantId}/telegram-groups`, body);
    return data;
  },
  updateTelegramGroup: async (tenantId: string, groupId: string, body: TelegramGroupUpdate): Promise<TelegramGroup> => {
    const { data } = await apiClient.patch<TelegramGroup>(`/api/v1/admin/tenants/${tenantId}/telegram-groups/${groupId}`, body);
    return data;
  },
  deleteTelegramGroup: async (tenantId: string, groupId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/tenants/${tenantId}/telegram-groups/${groupId}`);
  },

  // Email Config
  listEmailConfigs: async (tenantId: string): Promise<EmailConfig[]> => {
    const { data } = await apiClient.get<EmailConfig[]>(`/api/v1/admin/tenants/${tenantId}/email-configs`);
    return data;
  },
  createEmailConfig: async (tenantId: string, body: EmailConfigCreate): Promise<EmailConfig> => {
    const { data } = await apiClient.post<EmailConfig>(`/api/v1/admin/tenants/${tenantId}/email-configs`, body);
    return data;
  },
  updateEmailConfig: async (tenantId: string, configId: string, body: EmailConfigUpdate): Promise<EmailConfig> => {
    const { data } = await apiClient.patch<EmailConfig>(`/api/v1/admin/tenants/${tenantId}/email-configs/${configId}`, body);
    return data;
  },
  deleteEmailConfig: async (tenantId: string, configId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/tenants/${tenantId}/email-configs/${configId}`);
  },
  createEmailConfigItem: async (tenantId: string, configId: string, body: EmailConfigItemCreate): Promise<EmailConfig> => {
    const { data } = await apiClient.post<EmailConfig>(`/api/v1/admin/tenants/${tenantId}/email-configs/${configId}/items`, body);
    return data;
  },
  updateEmailConfigItem: async (tenantId: string, configId: string, itemId: string, body: EmailConfigItemUpdate): Promise<EmailConfig> => {
    const { data } = await apiClient.patch<EmailConfig>(`/api/v1/admin/tenants/${tenantId}/email-configs/${configId}/items/${itemId}`, body);
    return data;
  },
  deleteEmailConfigItem: async (tenantId: string, configId: string, itemId: string): Promise<EmailConfig> => {
    const { data } = await apiClient.delete<EmailConfig>(`/api/v1/admin/tenants/${tenantId}/email-configs/${configId}/items/${itemId}`);
    return data;
  },
};
